import { useEffect, useState, useCallback, useMemo } from "react";
import { motion } from "framer-motion";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import LoadingSpinner from "./LoadingSpinner";

const couples = [
  { id: 1, album: 3, src: 'https://spencer-w-mathews.github.io/rainbowtroutreverie_images/images/Jeanne+Leo/JL10.webp', albumTitle: "Jeanne + Leo", albumTitle2: 'Anniversary' },
  { id: 2, album: 1, src: 'https://spencer-w-mathews.github.io/rainbowtroutreverie_images/images/Desteni+Devin/DD10.webp', albumTitle: "Desteni + Devin" },
  { id: 3, album: 2, src: 'https://spencer-w-mathews.github.io/rainbowtroutreverie_images/images/Madeline+Caleb/MC7.webp', albumTitle: "Madeline + Caleb", albumTitle2: 'Engagement' },
];

const lifestyle = [
  { id: 1, album: 4, src: 'https://spencer-w-mathews.github.io/rainbowtroutreverie_images/images/Gower/25.webp', albumTitle: "The Gower Family" },
  { id: 3, album: 2, src: 'https://spencer-w-mathews.github.io/rainbowtroutreverie_images/images/Madeline+Caleb/MC7.webp', albumTitle: "Madeline + Caleb", albumTitle2: 'Engagement' },
  { id: 2, album: 1, src: 'https://spencer-w-mathews.github.io/rainbowtroutreverie_images/images/Desteni+Devin/DD10.webp', albumTitle: "Desteni + Devin" },
];

const nature = [
  { id: 2, album: 1, src: 'https://spencer-w-mathews.github.io/rainbowtroutreverie_images/images/Desteni+Devin/DD10.webp', albumTitle: "Desteni + Devin" },
  { id: 1, album: 3, src: 'https://spencer-w-mathews.github.io/rainbowtroutreverie_images/images/Jeanne+Leo/JL10.webp', albumTitle: "Jeanne + Leo", albumTitle2: 'Anniversary' },
  { id: 3, album: 2, src: 'https://spencer-w-mathews.github.io/rainbowtroutreverie_images/images/Madeline+Caleb/MC7.webp', albumTitle: "Madeline + Caleb", albumTitle2: 'Engagement' },
];

const home = [
  { id: 1, album: 4, src: 'https://spencer-w-mathews.github.io/rainbowtroutreverie_images/images/Gower/25.webp', albumTitle: "The Gower Family" },
  { id: 3, album: 2, src: 'https://spencer-w-mathews.github.io/rainbowtroutreverie_images/images/Madeline+Caleb/MC7.webp', albumTitle: "Madeline + Caleb", albumTitle2: 'Engagement' },
  { id: 2, album: 1, src: 'https://spencer-w-mathews.github.io/rainbowtroutreverie_images/images/Desteni+Devin/DD10.webp', albumTitle: "Desteni + Devin" },
  { id: 4, album: 3, src: 'https://spencer-w-mathews.github.io/rainbowtroutreverie_images/images/Jeanne+Leo/JL10.webp', albumTitle: "Jeanne + Leo", albumTitle2: 'Anniversary' },
];

const albumMap = { Couples: couples, Lifestyle: lifestyle, Nature: nature, "Creative Portraits": lifestyle, Home: home };

export default function PhotoCarousel({ header }) {
  const [index, setIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [loaded, setLoaded] = useState(false);
  const navigate = useNavigate();

  const images = useMemo(() => albumMap[header] || home, [header]);
  const prevIndex = (index - 1 + images.length) % images.length;
  const nextIndex = (index + 1) % images.length;

  const handleResize = useCallback(() => {
    setIsMobile(window.innerWidth <= 768);
  }, []);

  const preloadImage = useCallback((src) => {
    const img = new Image();
    img.src = src;
  }, []);

  useEffect(() => {
    images.forEach((img) => preloadImage(img.src));
    setLoaded(true);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [images, preloadImage, handleResize]);

  const nextSlide = useCallback(() => setIndex((i) => (i + 1) % images.length), [images.length]);
  const prevSlide = useCallback(() => setIndex((i) => (i - 1 + images.length) % images.length), [images.length]);
  const goToSlide = useCallback((target) => setIndex(target % images.length), [images.length]);

  if (!loaded) return <LoadingSpinner />;

  const textColor = window.location.pathname === '/portfolio' ? '#2e362f' : '#333';

  return (
    <Container>
      {isMobile ? (
        <MobileWrapper style={window.location.pathname === '/portfolio' ? {}:{marginLeft: '-18vw'}}>
          <SlideGroup>
            <ImageSmall as={motion.img} src={images[prevIndex].src} loading="lazy"style={{ flexShrink: 0 }}
              initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4 }} />
            <ActiveImage as={motion.img} src={images[index].src} loading="eager" style={{ flexShrink: 0 }}
              initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4 }}
              onClick={() => navigate(`/album/${images[index].album}`)}
              drag="x" dragElastic={0.2}
              dragConstraints={{ left: 0, right: 0 }}
              onDragEnd={(_, info) => {
                if (info.offset.x < -80) nextSlide();
                if (info.offset.x > 80) prevSlide();
              }} />
            <ImageSmall as={motion.img} src={images[nextIndex].src} loading="lazy" style={{ flexShrink: 0 }}
              initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4 }} />
          </SlideGroup>

          <Caption onClick={() => navigate(`/album/${images[index].album}`)} color={textColor}>
            <p>{images[index].albumTitle}</p>
            {images[index].albumTitle2 && <p style={{marginLeft: 5}}>{images[index].albumTitle2}</p>}
          </Caption>
          <ControlBar>
            <NavButton aria-label="Previous photo" onClick={prevSlide} color={textColor}>
              ←
            </NavButton>
            <Dots role="tablist" aria-label={`Portfolio images for ${header || "gallery"}`}>
              {images.map((image, dotIndex) => (
                <Dot
                  key={`${image.id}-${dotIndex}`}
                  aria-label={`Go to image ${dotIndex + 1}`}
                  aria-selected={dotIndex === index}
                  $active={dotIndex === index}
                  onClick={() => goToSlide(dotIndex)}
                />
              ))}
            </Dots>
            <NavButton aria-label="Next photo" onClick={nextSlide} color={textColor}>
              →
            </NavButton>
          </ControlBar>
        </MobileWrapper>
      ) : (
        <DesktopWrapper>
          <div style={{display: 'flex', alignItems: "center"}}>
            <Column>
              <ImageCircle as={motion.img} src={images[prevIndex].src} loading="lazy" />
              {window.location.pathname === '/portfolio' && <SideLeft onClick={prevSlide}>←</SideLeft>}
            </Column>
            <ActiveCircle as={motion.img} src={images[index].src} loading="eager"
              onClick={() => navigate(`/album/${images[index].album}`)}
              drag="x" dragElastic={0.2}
              dragConstraints={{ left: 0, right: 0 }}
              onDragEnd={(event, info) => {
              if (info.offset.x < -100) {
                // Swiped left → next
                nextSlide();
              } else if (info.offset.x > 100) {
                // Swiped right → prev
                prevSlide();
            }}} />
            <Column>
              <ImageCircle as={motion.img} src={images[nextIndex].src} loading="lazy" />
              {window.location.pathname === '/portfolio' && <SideRight onClick={nextSlide}>→</SideRight>}
            </Column>
          </div>
          <Caption onClick={() => navigate(`/album/${images[index].album}`)} color={textColor}>
            <p>{images[index].albumTitle}</p>
            {images[index].albumTitle2 && <p style={{marginLeft: 5}}>{images[index].albumTitle2}</p>}
          </Caption>
          <ControlBar>
            <NavButton aria-label="Previous photo" onClick={prevSlide} color={textColor}>
              ←
            </NavButton>
            <Dots role="tablist" aria-label={`Portfolio images for ${header || "gallery"}`}>
              {images.map((image, dotIndex) => (
                <Dot
                  key={`${image.id}-${dotIndex}`}
                  aria-label={`Go to image ${dotIndex + 1}`}
                  aria-selected={dotIndex === index}
                  $active={dotIndex === index}
                  onClick={() => goToSlide(dotIndex)}
                />
              ))}
            </Dots>
            <NavButton aria-label="Next photo" onClick={nextSlide} color={textColor}>
              →
            </NavButton>
          </ControlBar>
        </DesktopWrapper>
      )}
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: fit-content;
  margin-top: 20px;
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
`

const MobileWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 470px;
  overflow-x: hidden; 
`;

const DesktopWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  min-height: 470px;
`;

const SlideGroup = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  
`;

const ImageSmall = styled.img`
  width: clamp(90px, 25%, 150px);
  //border-radius: 50%;
  margin: 0 10px;
  box-shadow: 0 4px 10px rgba(0,0,0,0.3);
`;

const ActiveImage = styled(ImageSmall)`
  width: clamp(240px, 70%, 280px);
  //border-radius: 50%;
  transition: transform 0.2s ease;
  &:hover { transform: scale(1.02); }
`;

const ImageCircle = styled.img`
  width: clamp(80px, 10vw, 120px);
  //border-radius: 50%;
  margin: 0 10px;
  object-fit: cover;
  box-shadow: 0 4px 10px rgba(0,0,0,0.3);
`;

const ActiveCircle = styled(ImageCircle)`
  width: clamp(200px, 20vw, 280px);
  cursor: pointer;
  transition: transform 0.2s ease;
  &:hover { transform: scale(1.03); }
`;

const Caption = styled.div`
  text-align: center;
  cursor: pointer;
  color: ${({ color }) => color};
  margin-top: 8px;
  line-height: 1;
  display: flex;
`;

const ControlBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 12px;
`;

const NavButton = styled.button`
  border: 1px solid ${({ color }) => color || "#222"};
  background: transparent;
  color: ${({ color }) => color || "#222"};
  border-radius: 50%;
  width: 38px;
  height: 38px;
  display: grid;
  place-items: center;
  font-size: 1rem;
  cursor: pointer;
  transition: transform 0.2s ease, background 0.2s ease, color 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    background: ${({ color }) => color || "#222"};
    color: ${({ color }) => (color ? "#1f2722" : "#fdf9f1")};
  }
`;

const Dots = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Dot = styled.button`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 1px solid rgba(43, 52, 45, 0.2);
  background: ${({ $active }) => ($active ? "rgba(43, 52, 45, 0.9)" : "rgba(43, 52, 45, 0.18)")};
  cursor: pointer;
  transition: transform 0.2s ease, background 0.2s ease;

  &:hover {
    transform: scale(1.1);
    background: rgba(34, 40, 34, 0.7);
  }
`;

const SideLeft = styled.div`
  font-size: 40px;
  color: #eee;
  cursor: pointer;
  user-select: none;
  margin: 5px;
  margin-left: auto;
`;

const SideRight = styled.div`
  font-size: 40px;
  color: #eee;
  cursor: pointer;
  user-select: none;
  margin: 5px;
  margin-right: auto;
`;
 